<script setup lang="ts">
import type { JSONContent } from '@tiptap/vue-3';
import type { HeadingVariants as DsHeadingVariants } from '~ds/components/ui/typography';
import type { HeadingVariants } from '~kui/types/kui';

import { Heading } from '~ds/components/ui/typography';
import KUICoreInputRichTextEditorRendererHeading from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/KUICoreInputRichTextEditorRendererHeading.vue';
import { parseStringToTipTapJson } from '~kui/components/KUI/Core/Input/RichTextEditor/useRichTextEditor';

import type { RteRendererVariants } from './types';

import { RTE_RENDERER_VARIANT } from './types';
import { useRichTextEditorRender } from './useRichTextEditorRenderer';

interface Props {
  content: string;
  variant: RteRendererVariants;
  headingStyleSize?: HeadingVariants;
  headingSemanticSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  limit?: number | null;
  anchorHeadings?: boolean;
  renderBlocks?: boolean;
  mediaSizes?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  headingStyleSize: undefined,
  headingSemanticSize: undefined,
  limit: null,
  anchorHeadings: false,
  renderBlocks: false,
  mediaSizes: null,
});

const classes = computed(() => cva('break-words whitespace-normal')());

/** KUI heading ramp → DS Heading size, remapped by rank (pilot gap audit §B); the public prop keeps the KUI names. */
const DS_HEADING_SIZE: Record<HeadingVariants, NonNullable<DsHeadingVariants['size']>> = {
  banner: '3xl',
  h1: '2xl',
  h2: '2xl',
  h3: 'xl',
  h4: 'lg',
  h5: 'md',
  h6: 'sm',
};

const headingSize = computed(() => (props.headingStyleSize ? DS_HEADING_SIZE[props.headingStyleSize] : undefined));

const rteData = computed(() => {
  if (!props.content) {
    return null;
  }

  return parseStringToTipTapJson(props.content);
});

const getTextLength = (node: JSONContent): number => {
  if (node.type === 'text') {
    return (node.text || '').length;
  }
  return (node.content || []).reduce((sum, child) => sum + getTextLength(child), 0);
};

const truncateContent = (node: JSONContent, remaining: { value: number }): JSONContent | null => {
  if (remaining.value <= 0) {
    return null;
  }

  if (node.type === 'text') {
    const text = node.text || '';
    if (text.length <= remaining.value) {
      remaining.value -= text.length;
      return node;
    }
    const truncated = `${text.slice(0, remaining.value).trimEnd()}…`;
    remaining.value = 0;
    return { ...node, text: truncated };
  }

  if (!node.content) {
    return node;
  }

  const nextContent: JSONContent[] = [];
  for (const child of node.content) {
    const result = truncateContent(child, remaining);
    if (result) {
      nextContent.push(result);
    }
    if (remaining.value <= 0) {
      break;
    }
  }
  return { ...node, content: nextContent };
};

const totalTextLength = computed(() => (rteData.value ? getTextLength(rteData.value) : 0));

const canTruncate = computed(() =>
  props.limit !== null && props.limit > 0 && totalTextLength.value > props.limit,
);

const isExpanded = ref(false);

watch(() => props.content, () => {
  isExpanded.value = false;
});

const displayedData = computed<JSONContent | null>(() => {
  if (!rteData.value) {
    return null;
  }
  if (canTruncate.value && !isExpanded.value && props.limit !== null) {
    return truncateContent(rteData.value, { value: props.limit });
  }
  return rteData.value;
});

const showReadMore = computed(() => canTruncate.value && !isExpanded.value);

const { rootDocItems, renderNode } = useRichTextEditorRender(displayedData, {
  anchorHeadings: props.anchorHeadings,
  renderBlocks: props.renderBlocks,
  mediaSizes: props.mediaSizes,
});
</script>

<template>
  <div v-if="variant === RTE_RENDERER_VARIANT.PARAGRAPH" :class="classes">
    <template v-for="(node, index) in rootDocItems" :key="index">
      <component :is="renderNode(node)" />
    </template>
    <button
      v-if="showReadMore"
      type="button"
      class="cursor-pointer underline text-inherit"
      data-testid="rte-renderer-read-more"
      @click="isExpanded = true"
    >
      {{ $t('unified.common.readMore') }}
    </button>
  </div>
  <!-- there is only one heading -->
  <Heading
    v-else-if="rteData"
    :size="headingSize"
    :as="headingSemanticSize"
    :class="classes"
  >
    <KUICoreInputRichTextEditorRendererHeading :content="rteData" />
  </Heading>
</template>
